<div class="s09-card" *ngIf="mode==='list'">
  <div class="s09-list-head">
    <div>
      <div style="display:flex;align-items:baseline;gap:10px">
        <h2>Case lifecycles</h2>
      </div>
      <p class="s09-life-sub">One lifecycle per case type. Stages are its nodes, transitions its edges; automations hang off a transition.</p>
    </div>
  </div>

  <div class="s09-life-cols">
    <span>Case type</span>
    <span>Stages</span>
    <span>Transitions</span>
    <span>Automations</span>
    <span>Version</span>
    <span>Status</span>
    <span>Open cases</span>
    <span>Action</span>
  </div>

  <div *ngIf="loading" class="s09-empty">Loading case lifecycles…</div>
  <div *ngIf="!loading && !rows.length" class="s09-empty">
    <strong>No case lifecycles yet</strong>
    <span>{{ hint || 'Create a lifecycle for a case type, then add its stages.' }}</span>
  </div>

  <div class="s09-life-row" *ngFor="let row of rows">
    <strong class="s09-life-type">{{ row.type.name }}</strong>
    <span class="s09-life-chain">{{ row.chain }}</span>
    <span>{{ row.transitions }}</span>
    <span>—</span>
    <span>—</span>
    <span class="s09-life-status" [class.active]="row.status==='ACTIVE'" [class.draft]="row.status==='DRAFT'">{{ row.status }}</span>
    <span>—</span>
    <button type="button" class="s09-life-open" (click)="open(row)">Open</button>
  </div>
</div>

<div class="s09-lc" *ngIf="mode==='edit'">
  <div class="s09-lc-bar">
    <div class="s09-lc-bar-left">
      <select class="s09-lc-title" [ngModel]="caseTypeKey" (ngModelChange)="onTypeChange($event)">
        <option [ngValue]="null">New lifecycle · case type not set</option>
        <option *ngFor="let t of rows" [ngValue]="t.type.typeKey">{{ t.type.name }}</option>
      </select>
      <span class="s09-lc-rule"></span>
      <span class="s09-lc-count">{{ stageCountLabel() }} · {{ transitionLabel() }}</span>
      <span class="s09-lc-rule"></span>
      <span class="s09-lc-ver">V0</span>
      <span class="s09-lc-hint" *ngIf="hint">{{ hint }}</span>
    </div>
    <div class="s09-lc-bar-right">
      <button type="button" class="s09-lc-all" (click)="back()">All lifecycles</button>
      <button type="button" class="s09-lc-save" [class.dirty]="dirty" [disabled]="saving" (click)="save()">
        {{ saveLabel() }}
      </button>
    </div>
  </div>

  <div class="s09-lc-body">
    <aside class="s09-lc-col">
      <h3>Stages</h3>
      <p>Order is the order a case travels. Use the arrows to reorder, the clock on each stage is set in CFG-02.</p>
      <div class="s09-lc-stage-list">
        <div
          class="s09-lc-stage"
          *ngFor="let s of stages; let i = index"
          [class.on]="s.uid===selectedUid"
          (click)="select(s.uid)">
          <span class="s09-lc-no" [class.on]="s.uid===selectedUid">{{ i + 1 }}</span>
          <span class="s09-lc-stage-copy">
            <strong>{{ s.stage_name || 'Untitled stage' }}</strong>
            <em>{{ stageMeta(s) }}</em>
          </span>
          <span class="s09-lc-stage-tools" (click)="$event.stopPropagation()">
            <button type="button" title="Move up" [disabled]="i===0" (click)="move(i, -1)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 14l6-6 6 6"/></svg>
            </button>
            <button type="button" title="Move down" [disabled]="i===stages.length-1" (click)="move(i, 1)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 10l6 6 6-6"/></svg>
            </button>
            <button type="button" title="Remove" (click)="remove(i)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 7h16M9 7V5h6v2M8 7l1 12h6l1-12"/></svg>
            </button>
          </span>
        </div>
      </div>
      <button type="button" class="s09-lc-add" (click)="addStage()">+ Add stage</button>
    </aside>

    <section class="s09-lc-canvas">
      <div class="s09-lc-flow" *ngIf="stages.length; else noStages">
        <ng-container *ngFor="let s of stages; let i = index">
          <button
            type="button"
            class="s09-lc-card"
            [class.on]="s.uid===selectedUid"
            (click)="select(s.uid)">
            <span class="s09-lc-no on">{{ i + 1 }}</span>
            <span class="s09-lc-card-copy">
              <strong>{{ s.stage_name || 'Untitled stage' }}</strong>
              <em>{{ canvasNote(s) }}</em>
            </span>
            <span class="s09-lc-badge entry" *ngIf="isEntry(s)">Entry</span>
            <span class="s09-lc-badge terminal" *ngIf="isTerminal(s)">Terminal</span>
          </button>
          <div class="s09-lc-join" *ngIf="i < stages.length - 1">
            <span class="s09-lc-join-rail" aria-hidden="true"></span>
            <span class="s09-lc-join-label" *ngIf="moveNames(s).length">→ {{ moveNames(s).join(', ') }}</span>
          </div>
        </ng-container>
      </div>
      <ng-template #noStages>
        <div class="s09-lc-empty">Add a stage to start this lifecycle.</div>
      </ng-template>
    </section>

    <aside class="s09-lc-col s09-lc-inspector" *ngIf="selected as stage">
      <span class="s09-lc-kicker">Stage</span>
      <h2>{{ stage.stage_name || 'Untitled stage' }}</h2>

      <label class="s09-lc-field">
        <span>Stage name</span>
        <input [ngModel]="stage.stage_name" (ngModelChange)="rename(stage.uid, $event)" />
      </label>

      <div class="s09-lc-field">
        <span>Can move to</span>
        <label class="s09-lc-move" *ngFor="let other of others()" [class.on]="canMove(other.uid)">
          <input type="checkbox" [checked]="canMove(other.uid)" (change)="toggleMove(other.uid)" />
          <em>{{ other.stage_name || 'Untitled stage' }}</em>
        </label>
        <p class="s09-lc-note" *ngIf="!others().length">Add another stage to choose where this one can move.</p>
        <p class="s09-lc-note">A move the lifecycle does not allow is refused at publish, not at runtime. Backwards moves need a reason on the case.</p>
      </div>

      <div class="s09-lc-block">
        <span>Automations on these moves</span>
        <p>Nothing fires on a move out of this stage. Cases advance when a user moves them.</p>
        <button type="button" class="s09-lc-link">Attach an automation</button>
      </div>

      <div class="s09-lc-block">
        <span>Elsewhere</span>
        <p>The clock on this stage is set in CFG-02 along with the escalation ladder.</p>
        <button type="button" class="s09-lc-link">Set the clock in CFG-02</button>
      </div>
    </aside>
  </div>
</div>
